import React from 'react';
import { motion } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useLanguageStore } from '../../store/languageStore';
import { PatientListDto } from '../../types/patient.types';
import { User, Phone, Calendar, ArrowRight } from 'lucide-react';

interface PatientCardProps {
  patient: PatientListDto;
  index?: number;
}

export const PatientCard: React.FC<PatientCardProps> = ({ patient, index = 0 }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { isRTL } = useLanguageStore();

  return (
    <motion.div
      onClick={() => navigate(`/patients/${patient.id}`)}
      className="card shadow-sm border-0 h-100"
      style={{ cursor: 'pointer' }}
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.1, duration: 0.4 }}
      whileHover={{ y: -4, scale: 1.02 }}
      whileTap={{ scale: 0.98 }}
      dir={isRTL ? 'rtl' : 'ltr'}
    >
      <div className="card-body position-relative">
        {/* Header */}
        <div className="d-flex align-items-start justify-content-between mb-3" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
          <div className="d-flex align-items-center gap-3" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
            <motion.div
              className="rounded-circle bg-primary d-flex align-items-center justify-content-center text-white fw-bold shadow-sm"
              style={{ width: '48px', height: '48px' }}
              whileHover={{ rotate: 360 }}
              transition={{ duration: 0.6 }}
            >
              <User size={24} />
            </motion.div>
            <div>
              <h5 className="mb-0 fw-bold text-primary-hover">{patient.name}</h5>
              <small className="text-muted">{t('patients.patientId')}: #{patient.id}</small>
            </div>
          </div>
          
          <motion.div
            className="text-primary opacity-0 opacity-100-hover transition-opacity"
            whileHover={{ x: isRTL ? -4 : 4 }}
          >
            <ArrowRight size={20} />
          </motion.div>
        </div>

        {/* Details */}
        <div className="d-flex flex-column gap-2">
          <div className="d-flex align-items-center text-muted gap-2" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
            <Calendar size={16} />
            <small>
              {t('patients.age')}: <span className="fw-semibold text-dark">{patient.age}</span> {t('patients.years')}
            </small>
          </div>
          <div className="d-flex align-items-center text-muted gap-2" style={{ direction: isRTL ? 'rtl' : 'ltr' }}>
            <Phone size={16} />
            <small>{patient.phone}</small>
          </div>
          {patient.gender && (
            <div>
              <span className={`badge ${patient.gender === 'Male' ? 'bg-primary' : 'bg-danger'}`}>
                {patient.gender}
              </span>
            </div>
          )}
        </div>

        {/* Bottom border animation */}
        <motion.div
          className="position-absolute bottom-0 start-0 end-0"
          style={{ height: '3px', background: 'linear-gradient(to right, #0ea5e9, #0284c7)' }}
          initial={{ scaleX: 0 }}
          whileHover={{ scaleX: 1 }}
          transition={{ duration: 0.3 }}
        />
      </div>
    </motion.div>
  );
};
